import { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useProductTargets,
  useBatchTargetState,
  useUpdateTargetBid,
  useNegativeProductTargets,
  type ProductTarget,
  type NegativeProductTarget,
  type TargetFilters,
} from '@/api/targets'
import { parseExpression } from '@/api/targetExpression'
import { reconcileRequiredCount } from '@/api/client'
import { StateBadge } from '@/components/StatusBadge'
import DataTable, { type DataTableColumn } from '@/components/DataTable'
import Pagination from '@/components/Pagination'
import FilterBar from '@/components/FilterBar'
import DateRangePicker from '@/components/DateRangePicker'
import { useListQuery } from '@/lib/useListQuery'
import { Pause, Play, Archive, Plus, Check, X, AlertTriangle } from 'lucide-react'
import TargetDetailDrawer from './TargetDetailDrawer'
import NegativeTargetDrawer from './NegativeTargetDrawer'
import { IS_DEMO_MODE } from '@/lib/demoMode'
import { fmtEur, fmtPct, fmtInt } from './format'

function defaultDateRange(): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 30)
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) }
}

function ExpressionPill({ raw }: { raw: string }) {
  const { type, value } = parseExpression(raw)
  const colors: Record<string, string> = {
    asin: 'bg-blue-100 text-blue-800',
    category: 'bg-purple-100 text-purple-800',
    brand: 'bg-emerald-100 text-emerald-800',
    refinement: 'bg-amber-100 text-amber-800',
    unknown: 'bg-zinc-100 text-zinc-700',
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${colors[type] ?? colors['unknown']}`}
      title={raw}
    >
      <span className="uppercase tracking-wider">{type}</span>
      <span className="font-mono">{value || '—'}</span>
    </span>
  )
}

function BidCell({ targetId, currentBid }: { targetId: number; currentBid: string | null }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(currentBid ?? '')
  const updateBid = useUpdateTargetBid()

  function handleSave() {
    if (!value || value === currentBid) {
      setEditing(false)
      return
    }
    updateBid.mutate({ id: targetId, bid: value }, { onSuccess: () => setEditing(false) })
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          className="h-7 w-20 rounded border border-[var(--ring)] bg-[var(--background)] px-2 text-right text-sm tabular-nums outline-none"
        />
        <button onClick={handleSave} disabled={updateBid.isPending} className="text-emerald-600 hover:text-emerald-800">
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => {
            setEditing(false)
            setValue(currentBid ?? '')
          }}
          className="text-zinc-400 hover:text-zinc-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        if (!IS_DEMO_MODE) setEditing(true)
      }}
      disabled={IS_DEMO_MODE}
      title={IS_DEMO_MODE ? 'Demo-Modus aktiv — Bid-Änderung blockiert' : ''}
      className="tabular-nums hover:underline disabled:cursor-not-allowed disabled:no-underline"
    >
      {currentBid ? parseFloat(currentBid).toFixed(2) : '—'}
    </button>
  )
}

export default function TargetsList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = (searchParams.get('tab') ?? 'positive') as 'positive' | 'negatives'
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [negDrawerOpen, setNegDrawerOpen] = useState(false)

  function setTab(t: 'positive' | 'negatives') {
    const sp = new URLSearchParams(searchParams)
    sp.set('tab', t)
    setSearchParams(sp)
  }

  const defaults = defaultDateRange()
  const { state, setPage, setPageSize, toggleSort, setSearch, setDateRange, setFilter } = useListQuery({
    pageSize: 50,
    sortBy: 'spend',
    sortDir: 'desc',
  })
  const [searchInput, setSearchInput] = useState(state.search ?? '')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // Scheibe S (REST-O): Wie viele Eintraege des letzten Batch-Writes haben einen
  // ungeklaerten Amazon-Zustand (HTTP 502 / RECONCILE_REQUIRED)? 0 = kein Hinweis.
  const [reconcileCount, setReconcileCount] = useState(0)

  const filters: TargetFilters = useMemo(
    () => ({
      limit: state.pageSize,
      offset: state.page * state.pageSize,
      sortBy: state.sortBy ?? 'spend',
      sortDir: state.sortDir ?? 'desc',
      search: state.search,
      expressionType: state.filters['expressionType'] as TargetFilters['expressionType'],
      state: state.filters['state'],
      adType: state.filters['adType'] as TargetFilters['adType'],
      dateFrom: state.dateFrom ?? defaults.from,
      dateTo: state.dateTo ?? defaults.to,
    }),
    [state, defaults.from, defaults.to],
  )

  const positive = useProductTargets(filters)
  const negative = useNegativeProductTargets({
    limit: state.pageSize,
    offset: state.page * state.pageSize,
    search: state.search,
  })
  const batchState = useBatchTargetState()

  const positiveCount = positive.data?.total ?? 0
  const negativeCount = negative.data?.total ?? 0
  const positiveRows = positive.data?.productTargets ?? []
  const negativeRows = negative.data?.negativeProductTargets ?? []

  function onToggleAll() {
    const ids = positiveRows.map((t) => t.id)
    setSelected((prev) => (prev.size === ids.length ? new Set() : new Set(ids)))
  }

  function onToggleOne(id: string | number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id as number)) next.delete(id as number)
      else next.add(id as number)
      return next
    })
  }

  async function handleBatchState(st: string) {
    if (selected.size === 0) return
    setReconcileCount(0)
    try {
      await batchState.mutateAsync({ ids: [...selected], state: st })
    } catch (err) {
      // Scheibe S (REST-O): Dieser Aufruf lief bis hierher OHNE try/catch — ein
      // Fehlschlag verschwand im globalen Toast und riss zusaetzlich eine
      // unbehandelte Promise-Rejection auf.
      //
      // Auswahl BEWUSST erhalten: nach einem Fehlschlag muesste der Operator sie
      // sonst neu zusammensuchen, gerade dann, wenn Teile des Batches offen sind.
      // `reconcileRequiredCount` liefert nur fuer den Batch-502 der FORM 1
      // (`ackOrReconcile`) einen Wert > 0; ein `nack(502, …)` ohne `result` und
      // jeder andere Fehler ergeben 0 und bleiben beim gewohnten globalen Toast.
      setReconcileCount(reconcileRequiredCount(err))
      return
    }
    setSelected(new Set())
  }

  const positiveColumns: DataTableColumn<ProductTarget>[] = [
    { key: 'expression', label: 'Target', sortable: true, render: (t) => <ExpressionPill raw={t.expression} /> },
    {
      key: 'campaign_name',
      label: 'Kampagne',
      render: (t) => (
        <span className="truncate max-w-[200px] block text-[var(--muted-foreground)]" title={t.campaign_name}>
          {t.campaign_name}
        </span>
      ),
    },
    {
      key: 'ad_group_name',
      label: 'Ad Group',
      render: (t) => (
        <span className="truncate max-w-[160px] block text-[var(--muted-foreground)]" title={t.ad_group_name}>
          {t.ad_group_name}
        </span>
      ),
    },
    {
      key: 'ad_type',
      label: 'Typ',
      render: (t) => <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs uppercase">{t.ad_type}</span>,
    },
    { key: 'state', label: 'Status', render: (t) => <StateBadge state={t.state} /> },
    { key: 'bid', label: 'Bid', align: 'right', sortable: true, render: (t) => <BidCell targetId={t.id} currentBid={t.bid} /> },
    { key: 'spend', label: 'Spend', align: 'right', sortable: true, render: (t) => fmtEur(t.spend) },
    { key: 'sales', label: 'Sales', align: 'right', sortable: true, render: (t) => fmtEur(t.sales) },
    { key: 'orders', label: 'Orders', align: 'right', sortable: true, render: (t) => fmtInt(t.orders) },
    { key: 'acos', label: 'ACoS', align: 'right', sortable: true, render: (t) => fmtPct(t.acos) },
    { key: 'clicks', label: 'Clicks', align: 'right', sortable: true, render: (t) => fmtInt(t.clicks) },
  ]

  const negativeColumns: DataTableColumn<NegativeProductTarget>[] = [
    { key: 'expression', label: 'Negatives Target', render: (t) => <ExpressionPill raw={t.expression} /> },
    { key: 'level', label: 'Level', render: (t) => (t.ad_group_id ? 'Ad Group' : 'Kampagne') },
    { key: 'state', label: 'Status', render: (t) => <StateBadge state={t.state} /> },
    {
      key: 'created_at',
      label: 'Erstellt',
      render: (t) => new Date(t.created_at).toLocaleDateString('de-DE'),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">ASIN-Targeting</h1>
        <button
          onClick={() => (tab === 'positive' ? setDrawerOpen(true) : setNegDrawerOpen(true))}
          disabled={IS_DEMO_MODE}
          title={IS_DEMO_MODE ? 'Demo-Modus aktiv — Anlegen blockiert' : ''}
          className="inline-flex items-center gap-1 rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm text-[var(--primary-foreground)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> {tab === 'positive' ? 'Neues Target' : 'Neues negatives Target'}
        </button>
      </div>

      {/* Scheibe S (REST-O), Muster aus `CampaignList` (M9/Scheibe E): So erreicht ein
          ungeklaerter Amazon-Zustand aus `POST /targets/batch-state/` den Operator.
          Statt Toast — der verschwindet nach Sekunden, waehrend die betroffene
          Auswahl stehen bleibt. Bewusst KEIN Link auf den Prüfen-Tab: dessen
          Auswahl ist reiner Komponenten-State ohne URL. */}
      {reconcileCount > 0 && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{`${reconcileCount} Einträge prüfbedürftig — Amazon-Zustand ungeklärt. Details: Inbox → Prüfen.`}</span>
        </div>
      )}

      <div className="flex gap-2 border-b border-[var(--border)]">
        <button
          onClick={() => setTab('positive')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'positive'
              ? 'border-b-2 border-[var(--primary)] text-[var(--foreground)]'
              : 'text-[var(--muted-foreground)]'
          }`}
        >
          Positive ({positiveCount.toLocaleString('de-DE')})
        </button>
        <button
          onClick={() => setTab('negatives')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'negatives'
              ? 'border-b-2 border-[var(--primary)] text-[var(--foreground)]'
              : 'text-[var(--muted-foreground)]'
          }`}
        >
          Negatives ({negativeCount.toLocaleString('de-DE')})
        </button>
      </div>

      {tab === 'positive' ? (
        <>
          <FilterBar
            search={searchInput}
            onSearch={(v) => {
              setSearchInput(v)
              setSearch(v)
            }}
            searchPlaceholder="ASIN/Brand/Category suchen..."
            trailing={
              <DateRangePicker
                value={{ from: state.dateFrom ?? defaults.from, to: state.dateTo ?? defaults.to }}
                onChange={(r) => setDateRange(r.from, r.to)}
              />
            }
          >
            <select
              value={state.filters['expressionType'] ?? ''}
              onChange={(e) => setFilter('expressionType', e.target.value)}
              className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
            >
              <option value="">Alle Typen</option>
              <option value="asin">ASIN</option>
              <option value="category">Kategorie</option>
              <option value="brand">Marke</option>
              <option value="refinement">Refinement</option>
            </select>
            <select
              value={state.filters['adType'] ?? ''}
              onChange={(e) => setFilter('adType', e.target.value)}
              className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
            >
              <option value="">SP + SD</option>
              <option value="sp">SP</option>
              <option value="sd">SD</option>
            </select>
            <select
              value={state.filters['state'] ?? ''}
              onChange={(e) => setFilter('state', e.target.value)}
              className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
            >
              <option value="">Alle Status</option>
              <option value="enabled">Enabled</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </FilterBar>

          {selected.size > 0 && !IS_DEMO_MODE && (
            <div className="flex items-center gap-2 rounded-md bg-[var(--accent)] px-3 py-2 text-sm">
              <span className="font-medium">{selected.size} ausgewaehlt</span>
              <button
                onClick={() => handleBatchState('enabled')}
                className="ml-4 flex items-center gap-1 rounded px-2 py-1 text-emerald-700 hover:bg-emerald-100"
                disabled={batchState.isPending}
              >
                <Play className="h-3.5 w-3.5" /> Enable
              </button>
              <button
                onClick={() => handleBatchState('paused')}
                className="flex items-center gap-1 rounded px-2 py-1 text-amber-700 hover:bg-amber-100"
                disabled={batchState.isPending}
              >
                <Pause className="h-3.5 w-3.5" /> Pause
              </button>
              <button
                onClick={() => handleBatchState('archived')}
                className="flex items-center gap-1 rounded px-2 py-1 text-zinc-500 hover:bg-zinc-100"
                disabled={batchState.isPending}
              >
                <Archive className="h-3.5 w-3.5" /> Archivieren
              </button>
            </div>
          )}

          {positive.error ? (
            <div className="py-12 text-center text-sm text-red-500">Fehler: {(positive.error as Error).message}</div>
          ) : (
            <DataTable<ProductTarget>
              columns={positiveColumns}
              rows={positiveRows}
              getRowId={(t) => t.id}
              sortBy={state.sortBy}
              sortDir={state.sortDir}
              onSort={toggleSort}
              selection={{ selected: selected as Set<string | number>, onToggle: onToggleOne, onToggleAll }}
              loading={positive.isLoading}
              emptyMessage="Keine Product Targets gefunden"
            />
          )}

          <Pagination
            page={state.page}
            pageSize={state.pageSize}
            totalItems={positiveCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      ) : (
        <>
          <FilterBar
            search={searchInput}
            onSearch={(v) => {
              setSearchInput(v)
              setSearch(v)
            }}
            searchPlaceholder="Negatives Target suchen..."
          />
          {negative.error ? (
            <div className="py-12 text-center text-sm text-red-500">
              Fehler: {(negative.error as Error).message}
            </div>
          ) : (
            <DataTable<NegativeProductTarget>
              columns={negativeColumns}
              rows={negativeRows}
              getRowId={(t) => t.id}
              loading={negative.isLoading}
              emptyMessage="Keine negativen Product Targets gefunden"
            />
          )}
          <Pagination
            page={state.page}
            pageSize={state.pageSize}
            totalItems={negativeCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      <TargetDetailDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <NegativeTargetDrawer open={negDrawerOpen} onClose={() => setNegDrawerOpen(false)} />
    </div>
  )
}
